import { Alert, Box, Stack, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";

interface Props {
  qr: unknown;
}

/**
 * Renderiza el QR que devuelve el provider (ej. `qrInformation` de Dpago).
 * El shape no está estandarizado, así que renderizamos defensivamente en este orden:
 *  1. string/objeto que ya es una imagen (data: / http .png) → <img>
 *  2. string de payload EMV (ej. "00020101021..." de Dpago) → lo dibujamos como
 *     QR escaneable con qrcode.react (esto es lo que usa el modo embebido).
 *  3. cualquier otra cosa → mensaje informativo.
 */
export function QrView({ qr }: Props) {
  const img = extractImage(qr);
  const emv = extractEmvPayload(qr);

  return (
    <Stack spacing={2} alignItems="center" py={1}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Escaneá el QR para pagar
      </Typography>
      {img ? (
        <Box
          component="img"
          src={img}
          alt="QR de pago"
          sx={{ width: 240, height: 240, maxWidth: "100%", borderRadius: 2 }}
        />
      ) : emv ? (
        <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, lineHeight: 0 }}>
          <QRCodeCanvas value={emv} size={240} marginSize={0} level="M" />
        </Box>
      ) : (
        <Alert severity="info" sx={{ width: "100%" }}>
          El comercio recibirá la confirmación del pago. Seguí las instrucciones de tu billetera.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary" textAlign="center">
        El pago se confirma automáticamente cuando lo completás.
      </Typography>
    </Stack>
  );
}

/** Devuelve una URL/dataURI de imagen si el QR ya viene renderizado. */
function extractImage(qr: unknown): string | null {
  const isImg = (s: string) =>
    s.startsWith("data:image") || /^https?:\/\/\S+\.(png|jpg|jpeg|svg|gif)/i.test(s);
  if (typeof qr === "string" && isImg(qr)) return qr;
  if (qr && typeof qr === "object") {
    for (const key of ["image", "imageUrl", "qrImage", "base64", "png"]) {
      const v = (qr as Record<string, unknown>)[key];
      if (typeof v === "string" && (isImg(v) || v.startsWith("data:") || v.length > 100)) {
        return v.startsWith("data:") || isImg(v) ? v : `data:image/png;base64,${v}`;
      }
    }
  }
  return null;
}

/**
 * Extrae el payload EMV (texto crudo del QR) para dibujarlo nosotros.
 * Dpago lo manda en `qrInformation` como string; algunos providers lo anidan
 * en un objeto bajo claves típicas (`emv`, `payload`, `qrString`).
 */
function extractEmvPayload(qr: unknown): string | null {
  if (typeof qr === "string" && qr.trim().length > 0) return qr.trim();
  if (qr && typeof qr === "object") {
    for (const key of ["emv", "payload", "qrString", "qrData", "code"]) {
      const v = (qr as Record<string, unknown>)[key];
      if (typeof v === "string" && v.trim().length > 0) return v.trim();
    }
  }
  return null;
}
